import type { DailySnapshot, Field, FamiliarityRecord, PersistedStateV1, SetRecord } from "../types";
import { FIELDS } from "../types";

export interface FieldMeans {
  name: number;
  era: number;
  year: number;
}

export interface GroupSummary {
  key: string;
  label: string;
  count: number;
  overall: number;
  name: number;
  era: number;
  year: number;
}

function recordFor(id: string, familiarity: Record<string, FamiliarityRecord>): FamiliarityRecord {
  return familiarity[id] ?? { name: 0, era: 0, year: 0, seen: 0, lastSeen: null };
}

function mean(values: readonly number[]): number {
  return values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : 0;
}

export function fieldMeans(sets: readonly SetRecord[], familiarity: Record<string, FamiliarityRecord>): FieldMeans {
  return {
    name: mean(sets.map((set) => recordFor(set.id, familiarity).name)),
    era: mean(sets.map((set) => recordFor(set.id, familiarity).era)),
    year: mean(sets.map((set) => recordFor(set.id, familiarity).year)),
  };
}

export function overallMean(sets: readonly SetRecord[], familiarity: Record<string, FamiliarityRecord>): number {
  const fields = fieldMeans(sets, familiarity);
  return mean(FIELDS.map((field) => fields[field]));
}

export function groupedMeans(
  sets: readonly SetRecord[],
  familiarity: Record<string, FamiliarityRecord>,
  groupBy: "era" | "year",
): GroupSummary[] {
  const groups = new Map<string, SetRecord[]>();
  for (const set of sets) {
    const key = groupBy === "era" ? set.eraId : String(set.year);
    const group = groups.get(key) ?? [];
    group.push(set);
    groups.set(key, group);
  }
  return Array.from(groups.entries()).map(([key, group]) => {
    const fields = fieldMeans(group, familiarity);
    return {
      key,
      label: groupBy === "era" ? group[0].era : key,
      count: group.length,
      ...fields,
      overall: mean(FIELDS.map((field) => fields[field])),
    };
  });
}

export function todayLocal(date = new Date()): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function upsertDailySnapshot(snapshots: readonly DailySnapshot[], overall: number, date = todayLocal()): DailySnapshot[] {
  const next = [...snapshots.filter((snapshot) => snapshot.date !== date), { date, overall: Math.min(1, Math.max(0, overall)) }];
  return next.sort((a, b) => a.date.localeCompare(b.date)).slice(-30);
}

export function trendSnapshots(state: PersistedStateV1): DailySnapshot[] {
  return state.dailySnapshots.slice(-7);
}

export function historyAccuracy(state: PersistedStateV1): number {
  const grades = state.history.flatMap((entry) => Object.values(entry.result));
  return grades.length ? grades.filter((grade) => grade === "correct").length / grades.length : 0;
}

export function percent(value: number): number {
  return Math.round(Math.min(1, Math.max(0, value)) * 100);
}

export function fieldLabel(field: Field): string {
  return field === "name" ? "Set name" : field === "era" ? "Era" : "Year";
}
